import { Divider, Typography } from "@mui/material";
import CallAdditionalDocumentsCard from "@features/vessel-call/vessel-call-status/VesselCallDetails/CallAdditionalDocumentsCard";
import { getDocumentsWithToken } from "@services/azureDataApi";
import { SasTokenPrefix } from "@services/user/types";
import { useMutation } from "@tanstack/react-query";
import { t } from "i18next";
import { FORM_FIELDS } from "../../constants";

const LANG_BASE = "vessel-call.shipping-agency-transfer";

type ShippingAgencyTransferDetailsProps = {
  ownersMatters?: string;
  newShippingAgency: string;
  transferDate: string;
  additionalDocuments?: {
    originalFileName: string;
    url: string;
    fileFormat: string;
    type: string;
    specify: string;
    name: string;
    issueDate?: string | null;
    expiryDate?: string | null;
    summary: string;
  }[];
};

const ShippingAgencyTransferDetails: React.FC<ShippingAgencyTransferDetailsProps> = ({
  ownersMatters,
  newShippingAgency,
  transferDate,
  additionalDocuments,
}) => {
  const normalizeDocumentDate = (value?: string | Date | null) => {
    if (!value) return undefined;
    return value instanceof Date ? value.toISOString() : value;
  };
  const documentMutation = useMutation({
    mutationFn: async ({ url }: { service: string; url: string }) => {
      return getDocumentsWithToken(url, SasTokenPrefix.LICENSE_SERVICE);
    },
  });
  const handleDownload = async (service: string, documentUrl: string, filename?: string) => {
    try {
      if (!documentUrl) {
        console.error("Document URL is required");
        return;
      }

      const blob = await documentMutation.mutateAsync({ service, url: documentUrl });
      const defaultName = filename || "document.pdf";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = defaultName;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handlePreview = async (service: string, documentUrl: string) => {
    try {
      if (!documentUrl) {
        console.error("Document URL is required");
        return;
      }

      const blob = await documentMutation.mutateAsync({ service, url: documentUrl });
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Preview failed:", err);
    }
  };

  return (
    <div className="w-full  rounded-md border border-slate-200">
      <div className="px-6 pt-5 pb-3">
        <Typography variant="h6" fontWeight={600}>
          {t(`${LANG_BASE}.details`)}
        </Typography>
      </div>
      <div className="px-6 pb-5">
        {ownersMatters && (
          <>
            <div className="flex py-4">
              <div className="w-1/2">
                <Typography variant="body1" color="text.secondary">
                  {t(`${LANG_BASE}.form-fields.${FORM_FIELDS.OWNERS_MATTERS}`)}
                </Typography>
              </div>
              <div className="w-1/2">
                <Typography variant="body1" fontWeight={600}>
                  {ownersMatters}
                </Typography>
              </div>
            </div>
            <Divider />
          </>
        )}
        <div className="flex py-4">
          <div className="w-1/2">
            <Typography variant="body1" color="text.secondary">
              {t(`${LANG_BASE}.form-fields.${FORM_FIELDS.NEW_SHIPPING_AGENCY}`)}
            </Typography>
          </div>
          <div className="w-1/2">
            <Typography variant="body1" fontWeight={600}>
              {newShippingAgency}
            </Typography>
          </div>
        </div>
        <Divider />
        <div className="flex py-4">
          <div className="w-1/2">
            <Typography variant="body1" color="text.secondary">
              {t(`${LANG_BASE}.form-fields.${FORM_FIELDS.TRANSFER_DATE_AND_TIME}`)}
            </Typography>
          </div>
          <div className="w-1/2">
            <Typography variant="body1" fontWeight={600}>
              {new Date(transferDate).toLocaleString("en-US")}
            </Typography>
          </div>
        </div>
      </div>
      {(additionalDocuments?.length ?? 0) > 0 && (
        <div className="px-6 pt-5 pb-3">
          <Typography variant="h6" fontWeight={600}>
            {t(`${LANG_BASE}.additional-documents-title`)}
          </Typography>
        </div>
      )}
      <div className="grid grid-cols-2 gap-6 auto-rows-fr items-stretch">
        {additionalDocuments?.map((doc, idx) => {
          return (
            <div key={`${doc.name}-${idx}`} className="w-full h-full [&>*]:h-full">
              <CallAdditionalDocumentsCard
                testId={`Shipping-agency-transfer--additional-document-${idx}`}
                onPreview={() => handlePreview("bff-vessel-call", doc.url || "")}
                onDownload={() => handleDownload("bff-vessel-call", doc.url || "", doc.name || "")}
                fileName={doc.name || doc.originalFileName}
                issueDate={normalizeDocumentDate(doc.issueDate)}
                expiryDate={normalizeDocumentDate(doc.expiryDate)}
                documentType={doc.type}
                otherSpecifyType={doc.specify}
                documentSummary={doc.summary}
                documentName={doc.name}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShippingAgencyTransferDetails;
